import FilledInfoIcon from '@/assets/icons/FilledInfoIcon';
import NextButton from '@/components/Button/NextButton';
import { SubmissionOffer } from '@/types/offerWall';
import Image from 'next/image';
import { JSX } from 'react';
import SafeHTMLContent from '../DangerousHtml/DangerousHtml';
import OfferLabel from '../OfferLabel/OfferLabel';
import OfferTextInfo from '../OfferTextInfo/OfferTextInfo';
import useModal from '@/hooks/useModal';
import styles from './PrimaryInfoModal.module.scss';
import { MODAL_NAMES } from '@/constants/modals';

interface Props {
  isRecommend?: boolean;
  submissionOfferData?: SubmissionOffer;
  renderLabels?: (isBottom: boolean) => JSX.Element;
  speedBumpCondition?: boolean;
}

const PrimaryInfoModal = ({
  isRecommend = false,
  submissionOfferData,
  renderLabels,
  speedBumpCondition = false,
}: Props) => {
  const { addModal } = useModal();
  const renderApprovedLabel = () => {
    if (submissionOfferData?.preApproved) {
      return (
        <OfferLabel
          icon="pre-approved"
          text="Pre-approved"
          plusIcon={false}
          className={styles.approvedIcon}
        />
      );
    }
    if (submissionOfferData?.preQualified) {
      return (
        <OfferLabel
          icon="pre-approved"
          text="Pre-qualified"
          plusIcon={false}
          className={styles.approvedIcon}
        />
      );
    }
    return null;
  };

  if (!submissionOfferData) return null;

  return (
    <div className={`${styles.wrapper} ${isRecommend ? styles.recommend : ''}`}>
      <div className={styles.logoWrapper}>
        <Image
          src={submissionOfferData.originator.images[0].url}
          alt={`${submissionOfferData.originator.name} logo`}
          className={styles.logo}
          width={186}
          height={42}
          priority
        />
        {renderApprovedLabel()}
      </div>
      <div className={styles.line} />
      <div className={styles.detailsWrapper}>
        {submissionOfferData.maxAmount && (
          <OfferTextInfo
            title={`$${submissionOfferData.maxAmount.toLocaleString('en-US')}`}
            desc="Loan Amount"
          />
        )}
        {submissionOfferData.maxApr && (
          <OfferTextInfo
            title={`${submissionOfferData.maxApr}%`}
            desc="Fixed APR"
          />
        )}
        {submissionOfferData.monthlyPayment && (
          <OfferTextInfo
            title={`$${submissionOfferData.monthlyPayment}`}
            desc="Monthly Payment"
          />
        )}
        {submissionOfferData.termLength && (
          <OfferTextInfo
            title={`${submissionOfferData.termLength} ${submissionOfferData.termUnit}${submissionOfferData.termLength > 1 ? 's' : ''}`}
            desc="Term"
          />
        )}
      </div>
      {submissionOfferData.productSubType === 'secured_loan' &&
        speedBumpCondition && (
          <div className={styles.securedLoanSubtitle}>
            <FilledInfoIcon className={styles.securedLoanWarning} />
            <span className={styles.securedLoanLabel}>
              Heads up, this offer requires collateral, like a vehicle or
              housing fixtures, which lenders could use to qualify you for a
              lower APR
            </span>
          </div>
        )}
      <div className={styles.line} />
      <div className={styles.listWrapper}>
        {submissionOfferData.originator.description && (
          <p className={styles.listText}>
            <span>Offer details</span>
          </p>
        )}
        {submissionOfferData.originator.description && (
          <div style={{ opacity: 0.6 }}>
            <SafeHTMLContent
              html={submissionOfferData.originator.description}
              styles={styles.listText}
            />
          </div>
        )}
        {submissionOfferData.originator.disclaimer && (
          <div style={{ opacity: 0.6 }}>
            <SafeHTMLContent
              html={submissionOfferData.originator.disclaimer}
              styles={styles.listText}
            />
          </div>
        )}
      </div>
      <div className={styles.line} />
      <div className={styles.buttonsWrapper}>
        {submissionOfferData.secured && speedBumpCondition ? (
          <NextButton
            type="button"
            text="Continue"
            onClick={() =>
              addModal({
                name: MODAL_NAMES.securedOffer,
                data: {
                  url: submissionOfferData.url,
                },
              })
            }
            styles={styles.button}
            isArrow
          />
        ) : (
          <NextButton
            text="Continue"
            type="link"
            target="_blank"
            link={submissionOfferData.url}
            styles={styles.button}
            isArrow
          />
        )}
        <div className={styles.labelsWrapper}>{renderLabels?.(true)}</div>
      </div>
    </div>
  );
};

export default PrimaryInfoModal;
